import { useEffect, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Loader2, Save, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { fetchContent, mergeContent, adminUpdateContent, adminResetContent, adminUndoResetContent, adminGenerateComposite } from "@/lib/api";
import { FALLBACK_CONTENT, ICON_OPTIONS } from "@/data/site";

const clone = (o) => JSON.parse(JSON.stringify(o));

const Field = ({ label, value, onChange, textarea, placeholder, testid }) => (
  <div>
    <Label className="text-xs font-medium text-foreground">{label}</Label>
    {textarea ? (
      <Textarea className="mt-1 min-h-20 text-foreground" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    ) : (
      <Input className="mt-1 h-10 text-foreground" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    )}
  </div>
);

const IconPicker = ({ value, onChange }) => (
  <div>
    <Label className="text-xs font-medium text-foreground">Icon</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
      <SelectContent className="dark">
        {ICON_OPTIONS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const SectionCard = ({ children }) => (
  <Card className="space-y-4 rounded-xl border-border bg-card p-4">{children}</Card>
);

const COMPOSITE_BG_OPTIONS = [
  { value: "botanical", label: "Green botanical (default)" },
  { value: "tropical", label: "Tropical leaves" },
  { value: "spa", label: "Spa & stones" },
  { value: "sunrise", label: "Golden sunrise" },
];
const COMPOSITE_LAYOUT_OPTIONS = [
  { value: "artistic", label: "Artistic (depth)" },
  { value: "row", label: "Product row" },
  { value: "flatlay", label: "Flat lay" },
];

// AI generator that composites the 4 product photos onto a nature background.
const CompositeGenerator = ({ orientation = "portrait", onGenerated, testid }) => {
  const [background, setBackground] = useState("botanical");
  const [layout, setLayout] = useState("artistic");
  const [generating, setGenerating] = useState(false);

  const run = async () => {
    setGenerating(true);
    try {
      const res = await adminGenerateComposite({ background, layout, orientation });
      onGenerated(res.url);
      toast.success("Bagong composite na-generate! I-save para permanente.");
    } catch (err) {
      toast.error("Hindi na-generate. Subukang muli.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-border bg-background/60 p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold text-foreground">AI composite mula sa 4 produkto</p>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">Nili-linis ang background ng bawat produkto tapos pinagsasama sa nature background. (~30s)</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-[11px] text-muted-foreground">Background</Label>
          <Select value={background} onValueChange={setBackground}>
            <SelectTrigger className="mt-1 h-9" data-testid={testid ? `${testid}-bg` : undefined}><SelectValue /></SelectTrigger>
            <SelectContent className="dark">
              {COMPOSITE_BG_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Arrangement</Label>
          <Select value={layout} onValueChange={setLayout}>
            <SelectTrigger className="mt-1 h-9" data-testid={testid ? `${testid}-layout` : undefined}><SelectValue /></SelectTrigger>
            <SelectContent className="dark">
              {COMPOSITE_LAYOUT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button type="button" size="sm" className="mt-3" onClick={run} disabled={generating} data-testid={testid ? `${testid}-run` : undefined}>
        {generating ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Nage-generate...</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Generate variation</>}
      </Button>
    </div>
  );
};

const ArrayItem = ({ children, onRemove }) => (
  <div className="relative rounded-lg border border-border bg-background/60 p-4">
    <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-2 h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove}>
      <Trash2 className="h-4 w-4" />
    </Button>
    <div className="space-y-3 pr-8">{children}</div>
  </div>
);

export const ContentEditor = ({ onSaved }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent()
      .then((c) => setDraft(mergeContent(FALLBACK_CONTENT, c)))
      .catch(() => setDraft(clone(FALLBACK_CONTENT)))
      .finally(() => setLoading(false));
  }, []);

  const setPath = (updater) => setDraft((d) => { const nd = clone(d); updater(nd); return nd; });

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateContent(draft);
      setDraft(mergeContent(FALLBACK_CONTENT, res));
      toast.success("Na-save ang mga pagbabago!");
      onSaved && onSaved();
    } catch (err) {
      toast.error("Hindi na-save. Pakisubukang muli.");
    } finally {
      setSaving(false);
    }
  };

  const undoReset = async () => {
    try {
      const res = await adminUndoResetContent();
      setDraft(mergeContent(FALLBACK_CONTENT, res));
      toast.success("Naibalik ang dating content");
      onSaved && onSaved();
    } catch (err) {
      toast.error("Walang backup na maibabalik");
    }
  };

  // Reset is SAFE: backend archives the current content first, so it can be undone.
  const reset = async () => {
    try {
      const res = await adminResetContent();
      setDraft(mergeContent(FALLBACK_CONTENT, res));
      toast.success("Naibalik sa default", { description: "Naka-backup ang dating content.", action: { label: "I-undo", onClick: undoReset }, duration: 10000 });
      onSaved && onSaved();
    } catch (err) {
      toast.error("Hindi na-reset");
    }
  };

  if (loading || !draft) {
    return <div className="p-10 text-center text-sm text-muted-foreground">Naglo-load ng content...</div>;
  }

  const b = draft.brand, h = draft.hero, ab = draft.about, pr = draft.products, op = draft.opportunity, ts = draft.testimonials, fq = draft.faq, ct = draft.contact, ft = draft.footer, pm = draft.promo || {}, pay = draft.payment || {}, pmn = draft.productMenu || {}, am = draft.aboutMe || {};
  const setAboutMe = (fn) => setPath((d) => { d.aboutMe = d.aboutMe || { paragraphs: [], roles: [] }; d.aboutMe.paragraphs = d.aboutMe.paragraphs || []; d.aboutMe.roles = d.aboutMe.roles || []; fn(d.aboutMe); });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Nilalaman ng site</h2>
          <p className="text-sm text-muted-foreground">I-edit ang mga teksto at larawan, tapos i-save. Agad itong makikita sa landing page.</p>
        </div>
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="content-reset-button"><RotateCcw className="mr-2 h-4 w-4" /> Reset</Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="content-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>I-reset sa default ang lahat ng text at images?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ise-save muna namin ang backup ng kasalukuyang content, kaya pwede mo itong i-undo pagkatapos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="content-reset-cancel">Kanselahin</AlertDialogCancel>
                <AlertDialogAction onClick={reset} data-testid="content-reset-confirm">Oo, i-reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={save} disabled={saving} data-testid="content-save-button">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-save...</> : <><Save className="mr-2 h-4 w-4" /> Save all</>}
          </Button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["brand", "hero"]} className="space-y-3">
        {/* BRAND & CONTACT */}
        <AccordionItem value="brand" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="content-section-brand">Branding at Contact</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Brand name" value={b.brandName} onChange={(v) => setPath((d) => { d.brand.brandName = v; })} testid="content-brand-name" />
                <Field label="Pangalan mo (distributor)" value={b.distributorName} onChange={(v) => setPath((d) => { d.brand.distributorName = v; })} testid="content-distributor-name" />
                <Field label="Role / titulo" value={b.role} onChange={(v) => setPath((d) => { d.brand.role = v; })} />
                <Field label="Lokasyon" value={b.location} onChange={(v) => setPath((d) => { d.brand.location = v; })} />
                <Field label="Numero" value={b.phone} onChange={(v) => setPath((d) => { d.brand.phone = v; })} />
                <Field label="Email" value={b.email} onChange={(v) => setPath((d) => { d.brand.email = v; })} />
                <Field label="WhatsApp link" value={b.whatsapp} onChange={(v) => setPath((d) => { d.brand.whatsapp = v; })} />
                <Field label="Messenger link" value={b.messenger} onChange={(v) => setPath((d) => { d.brand.messenger = v; })} />
                <Field label="Facebook link" value={b.facebook} onChange={(v) => setPath((d) => { d.brand.facebook = v; })} />
                <Field label="Instagram link" value={b.instagram} onChange={(v) => setPath((d) => { d.brand.instagram = v; })} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ImageUploader label="Photo mo (distributor) — lalabas sa MAG-USAP TAYO" value={b.distributorPhoto} onChange={(v) => setPath((d) => { d.brand.distributorPhoto = v; })} testid="content-distributor-photo" square />
                <p className="self-end text-xs text-muted-foreground">Square photo (hal. 600x600) ang pinakamaganda. Kapag blangko, initials ang lalabas.</p>
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* HERO */}
        <AccordionItem value="hero" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="content-section-hero">Hero (pinakataas)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Badge (blangko = brand + lokasyon)" value={h.badge} onChange={(v) => setPath((d) => { d.hero.badge = v; })} />
              <Field label="Headline" value={h.headline} onChange={(v) => setPath((d) => { d.hero.headline = v; })} textarea testid="content-hero-headline" />
              <Field label="Subhead" value={h.subhead} onChange={(v) => setPath((d) => { d.hero.subhead = v; })} textarea />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Shop button" value={h.ctaShop} onChange={(v) => setPath((d) => { d.hero.ctaShop = v; })} />
                <Field label="Join button" value={h.ctaJoin} onChange={(v) => setPath((d) => { d.hero.ctaJoin = v; })} />
                <Field label="Stat value" value={h.statValue} onChange={(v) => setPath((d) => { d.hero.statValue = v; })} />
                <Field label="Stat label" value={h.statLabel} onChange={(v) => setPath((d) => { d.hero.statLabel = v; })} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ImageUploader label="Main image" value={h.imageMain} onChange={(v) => setPath((d) => { d.hero.imageMain = v; })} testid="content-hero-image-main" />
                <ImageUploader label="Secondary image" value={h.imageSecondary} onChange={(v) => setPath((d) => { d.hero.imageSecondary = v; })} testid="content-hero-image-secondary" square />
              </div>
              <CompositeGenerator orientation="portrait" testid="hero-composite-generator" onGenerated={(url) => setPath((d) => { d.hero.imageMain = url; })} />
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-xs font-medium text-foreground">Trust chips</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.hero.chips.push({ label: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
                </div>
                <div className="space-y-2">
                  {h.chips.map((c, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="h-10" value={c.label} onChange={(e) => setPath((d) => { d.hero.chips[i].label = e.target.value; })} />
                      <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground hover:text-destructive" onClick={() => setPath((d) => { d.hero.chips.splice(i, 1); })}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* PROMO BANNER */}
        <AccordionItem value="promo" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="content-section-promo">Promo Banner (taas ng site)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <div className="flex items-center gap-3">
                <Switch checked={!!pm.enabled} onCheckedChange={(v) => setPath((d) => { d.promo.enabled = v; })} id="promo-enabled" data-testid="content-promo-enabled" />
                <Label htmlFor="promo-enabled" className="text-sm">Ipakita ang promo banner sa taas</Label>
              </div>
              <Field label="Teksto ng promo" value={pm.text} onChange={(v) => setPath((d) => { d.promo.text = v; })} textarea testid="content-promo-text" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Link label (blangko = walang link)" value={pm.linkLabel} onChange={(v) => setPath((d) => { d.promo.linkLabel = v; })} />
                <Field label="Link target (join / products / contact o buong URL)" value={pm.linkTarget} onChange={(v) => setPath((d) => { d.promo.linkTarget = v; })} />
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* PAYMENT (GCASH) */}
        <AccordionItem value="payment" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="content-section-payment">Payment (GCash)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <div className="flex items-center gap-3">
                <Switch checked={!!pay.gcashEnabled} onCheckedChange={(v) => setPath((d) => { d.payment.gcashEnabled = v; })} id="gcash-enabled" data-testid="content-gcash-enabled" />
                <Label htmlFor="gcash-enabled" className="text-sm">I-enable ang GCash checkout (kapag OFF, inquiry/order form lang)</Label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="GCash name (account holder)" value={pay.gcashName} onChange={(v) => setPath((d) => { d.payment.gcashName = v; })} testid="content-gcash-name" />
                <Field label="GCash number" value={pay.gcashNumber} onChange={(v) => setPath((d) => { d.payment.gcashNumber = v; })} testid="content-gcash-number" />
              </div>
              <ImageUploader label="GCash QR code (optional)" value={pay.gcashQr} onChange={(v) => setPath((d) => { d.payment.gcashQr = v; })} testid="content-gcash-qr" square />
              <Field label="Instructions sa customer" value={pay.instructions} onChange={(v) => setPath((d) => { d.payment.instructions = v; })} textarea />
              <p className="text-xs font-medium text-foreground">Palitan ang GCash name at number ng totoo mong account. Ilalagay ng customer ang GCash Reference No. pagkatapos magbayad, tapos mo ito ive-verify sa Orders tab.</p>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* ABOUT */}
        <AccordionItem value="about" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">About / Bakit Ecentra</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={ab.eyebrow} onChange={(v) => setPath((d) => { d.about.eyebrow = v; })} />
              <Field label="Heading" value={ab.heading} onChange={(v) => setPath((d) => { d.about.heading = v; })} />
              <Field label="Body" value={ab.body} onChange={(v) => setPath((d) => { d.about.body = v; })} textarea />
              <ImageUploader label="Section image (Bakit Ecentra)" value={ab.image} onChange={(v) => setPath((d) => { d.about.image = v; })} testid="content-about-image" />
              <CompositeGenerator orientation="landscape" testid="about-composite-generator" onGenerated={(url) => setPath((d) => { d.about.image = url; })} />
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Mga highlight</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.about.items.push({ icon: "leaf", title: "", desc: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ab.items.map((it, i) => (
                  <ArrayItem key={i} onRemove={() => setPath((d) => { d.about.items.splice(i, 1); })}>
                    <IconPicker value={it.icon} onChange={(v) => setPath((d) => { d.about.items[i].icon = v; })} />
                    <Field label="Title" value={it.title} onChange={(v) => setPath((d) => { d.about.items[i].title = v; })} />
                    <Field label="Description" value={it.desc} onChange={(v) => setPath((d) => { d.about.items[i].desc = v; })} textarea />
                  </ArrayItem>
                ))}
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* PRODUCTS SECTION TEXT */}
        <AccordionItem value="products" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">Products section (teksto)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={pr.eyebrow} onChange={(v) => setPath((d) => { d.products.eyebrow = v; })} />
              <Field label="Heading" value={pr.heading} onChange={(v) => setPath((d) => { d.products.heading = v; })} />
              <Field label="Body" value={pr.body} onChange={(v) => setPath((d) => { d.products.body = v; })} textarea />
              <Field label="Promo price badge (label sa tabi ng presyo)" value={pr.priceLabel} onChange={(v) => setPath((d) => { d.products.priceLabel = v; })} placeholder="Promo Price" testid="content-products-pricelabel" />
              <Field label="Price-change notice (blangko = itago)" value={pr.promoNote} onChange={(v) => setPath((d) => { d.products.promoNote = v; })} textarea placeholder="Promotional price — mag-iiba ang presyo anytime soon." testid="content-products-promonote" />
              <Field label="Compliance note sa product detail at checkout (blangko = itago)" value={pr.disclaimer} onChange={(v) => setPath((d) => { d.products.disclaimer = v; })} placeholder="Hindi gamot. Results may vary." testid="content-products-disclaimer" />
              <p className="text-xs font-medium text-foreground">Ang mga produkto mismo ay ina-edit sa tab na “Produkto”.</p>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* PRODUCT MENU */}
        <AccordionItem value="productMenu" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">Product menu (teksto)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={pmn.eyebrow} onChange={(v) => setPath((d) => { d.productMenu = { ...(d.productMenu || {}), eyebrow: v }; })} />
              <Field label="Heading" value={pmn.heading} onChange={(v) => setPath((d) => { d.productMenu = { ...(d.productMenu || {}), heading: v }; })} />
              <Field label="Body" value={pmn.body} onChange={(v) => setPath((d) => { d.productMenu = { ...(d.productMenu || {}), body: v }; })} textarea />
              <p className="text-xs font-medium text-foreground">Ang features, advantages, at science ng bawat produkto ay ina-edit sa tab na “Produkto”.</p>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* OPPORTUNITY */}
        <AccordionItem value="opportunity" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">Opportunity / Sali sa Team</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={op.eyebrow} onChange={(v) => setPath((d) => { d.opportunity.eyebrow = v; })} />
              <Field label="Heading" value={op.heading} onChange={(v) => setPath((d) => { d.opportunity.heading = v; })} textarea />
              <Field label="Body" value={op.body} onChange={(v) => setPath((d) => { d.opportunity.body = v; })} textarea />
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Benefits</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.opportunity.benefits.push({ icon: "wallet", title: "", desc: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {op.benefits.map((it, i) => (
                  <ArrayItem key={i} onRemove={() => setPath((d) => { d.opportunity.benefits.splice(i, 1); })}>
                    <IconPicker value={it.icon} onChange={(v) => setPath((d) => { d.opportunity.benefits[i].icon = v; })} />
                    <Field label="Title" value={it.title} onChange={(v) => setPath((d) => { d.opportunity.benefits[i].title = v; })} />
                    <Field label="Description" value={it.desc} onChange={(v) => setPath((d) => { d.opportunity.benefits[i].desc = v; })} textarea />
                  </ArrayItem>
                ))}
              </div>
              <Field label="Steps title" value={op.stepsTitle} onChange={(v) => setPath((d) => { d.opportunity.stepsTitle = v; })} />
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Steps</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.opportunity.steps.push({ step: "", title: "", desc: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {op.steps.map((it, i) => (
                  <ArrayItem key={i} onRemove={() => setPath((d) => { d.opportunity.steps.splice(i, 1); })}>
                    <Field label="No." value={it.step} onChange={(v) => setPath((d) => { d.opportunity.steps[i].step = v; })} />
                    <Field label="Title" value={it.title} onChange={(v) => setPath((d) => { d.opportunity.steps[i].title = v; })} />
                    <Field label="Description" value={it.desc} onChange={(v) => setPath((d) => { d.opportunity.steps[i].desc = v; })} textarea />
                  </ArrayItem>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Form heading" value={op.formHeading} onChange={(v) => setPath((d) => { d.opportunity.formHeading = v; })} />
                <Field label="Form subtext" value={op.formSub} onChange={(v) => setPath((d) => { d.opportunity.formSub = v; })} />
                <Field label="Success heading" value={op.successHeading} onChange={(v) => setPath((d) => { d.opportunity.successHeading = v; })} />
                <Field label="Success body" value={op.successBody} onChange={(v) => setPath((d) => { d.opportunity.successBody = v; })} textarea />
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* ABOUT ME / KWENTO NI SAM */}
        <AccordionItem value="aboutMe" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline" data-testid="content-section-aboutme">Kwento ni Sam (About Me — katabi ng Join form)</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <div className="flex items-center gap-3">
                <Switch checked={am.enabled !== false} onCheckedChange={(v) => setAboutMe((a) => { a.enabled = v; })} id="aboutme-enabled" data-testid="content-aboutme-enabled" />
                <Label htmlFor="aboutme-enabled" className="text-sm">Ipakita ang personal na kwento sa tabi ng "Join My Team" form</Label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Eyebrow" value={am.eyebrow} onChange={(v) => setAboutMe((a) => { a.eyebrow = v; })} />
                <Field label="Pagbati / heading" value={am.greeting} onChange={(v) => setAboutMe((a) => { a.greeting = v; })} testid="content-aboutme-greeting" />
              </div>
              <Field label="Tagline (italic sa ilalim ng heading)" value={am.tagline} onChange={(v) => setAboutMe((a) => { a.tagline = v; })} />
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-xs font-medium text-foreground">Mga talata ng kwento</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setAboutMe((a) => { a.paragraphs.push(""); })} data-testid="content-aboutme-add-paragraph"><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag talata</Button>
                </div>
                <div className="space-y-3">
                  {(am.paragraphs || []).map((p, i) => (
                    <ArrayItem key={i} onRemove={() => setAboutMe((a) => { a.paragraphs.splice(i, 1); })}>
                      <Field label={`Talata ${i + 1}`} value={p} onChange={(v) => setAboutMe((a) => { a.paragraphs[i] = v; })} textarea testid={`content-aboutme-paragraph-${i}`} />
                    </ArrayItem>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-xs font-medium text-foreground">Mga role / titulo (chips)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setAboutMe((a) => { a.roles.push(""); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
                </div>
                <div className="space-y-2">
                  {(am.roles || []).map((r, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="h-10" value={r} onChange={(e) => setAboutMe((a) => { a.roles[i] = e.target.value; })} />
                      <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground hover:text-destructive" onClick={() => setAboutMe((a) => { a.roles.splice(i, 1); })}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Pangalan sa signature" value={am.signatureName} onChange={(v) => setAboutMe((a) => { a.signatureName = v; })} />
                <Field label="Role sa ilalim ng signature" value={am.signatureRole} onChange={(v) => setAboutMe((a) => { a.signatureRole = v; })} />
                <Field label="CTA button label" value={am.ctaLabel} onChange={(v) => setAboutMe((a) => { a.ctaLabel = v; })} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ImageUploader label="Photo (blangko = gamitin ang distributor photo)" value={am.photo} onChange={(v) => setAboutMe((a) => { a.photo = v; })} testid="content-aboutme-photo" square />
                <p className="self-end text-xs text-muted-foreground">Kung gusto mo ng ibang photo dito (hal. with team o with products), i-upload dito. Kapag blangko, ang portrait mula sa "Branding at Contact" ang gagamitin.</p>
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* TESTIMONIALS */}
        <AccordionItem value="testimonials" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">Testimonials</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={ts.eyebrow} onChange={(v) => setPath((d) => { d.testimonials.eyebrow = v; })} />
              <Field label="Heading" value={ts.heading} onChange={(v) => setPath((d) => { d.testimonials.heading = v; })} />
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Mga review</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.testimonials.items.push({ name: "", role: "", quote: "", avatar: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="space-y-3">
                {ts.items.map((it, i) => (
                  <ArrayItem key={i} onRemove={() => setPath((d) => { d.testimonials.items.splice(i, 1); })}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="Name" value={it.name} onChange={(v) => setPath((d) => { d.testimonials.items[i].name = v; })} />
                      <Field label="Role / lokasyon" value={it.role} onChange={(v) => setPath((d) => { d.testimonials.items[i].role = v; })} />
                    </div>
                    <Field label="Quote" value={it.quote} onChange={(v) => setPath((d) => { d.testimonials.items[i].quote = v; })} textarea />
                    <ImageUploader label="Avatar" value={it.avatar} onChange={(v) => setPath((d) => { d.testimonials.items[i].avatar = v; })} square />
                  </ArrayItem>
                ))}
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* FAQ */}
        <AccordionItem value="faq" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">FAQ</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Eyebrow" value={fq.eyebrow} onChange={(v) => setPath((d) => { d.faq.eyebrow = v; })} />
              <Field label="Heading" value={fq.heading} onChange={(v) => setPath((d) => { d.faq.heading = v; })} />
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Mga tanong</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setPath((d) => { d.faq.items.push({ q: "", a: "" }); })}><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="space-y-3">
                {fq.items.map((it, i) => (
                  <ArrayItem key={i} onRemove={() => setPath((d) => { d.faq.items.splice(i, 1); })}>
                    <Field label="Tanong" value={it.q} onChange={(v) => setPath((d) => { d.faq.items[i].q = v; })} />
                    <Field label="Sagot" value={it.a} onChange={(v) => setPath((d) => { d.faq.items[i].a = v; })} textarea />
                  </ArrayItem>
                ))}
              </div>
            </SectionCard>
          </AccordionContent>
        </AccordionItem>

        {/* CONTACT + FOOTER */}
        <AccordionItem value="contact" className="rounded-xl border border-border bg-card px-4">
          <AccordionTrigger className="hover:no-underline">Contact at Footer</AccordionTrigger>
          <AccordionContent>
            <SectionCard>
              <Field label="Contact eyebrow" value={ct.eyebrow} onChange={(v) => setPath((d) => { d.contact.eyebrow = v; })} />
              <Field label="Contact heading" value={ct.heading} onChange={(v) => setPath((d) => { d.contact.heading = v; })} />
              <Field label="Contact body" value={ct.body} onChange={(v) => setPath((d) => { d.contact.body = v; })} textarea />
              <Field label="Footer tagline" value={ft.tagline} onChange={(v) => setPath((d) => { d.footer.tagline = v; })} textarea />
              <Field label="Footer disclaimer (maliit na text sa pinakababa)" value={ft.disclaimer} onChange={(v) => setPath((d) => { d.footer.disclaimer = v; })} textarea testid="content-footer-disclaimer" />
            </SectionCard>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="content-save-button-bottom">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-save...</> : <><Save className="mr-2 h-4 w-4" /> Save all</>}
        </Button>
      </div>
    </div>
  );
};
